import React, { useEffect, useState, useRef } from "react";
import ChatInput from "../../../components/chatInput";
import { getThemeColors, Theme } from "../../../theme/colors";
import { getLogoImages } from "../../../layout/chatSidebar";
import { mergeStyles } from "@fluentui/react";
import axios from "axios";
import config from "../../../config";
import { ErrorResponseData, ChatInputData } from "../../chatPage";
import Markdown from "markdown-to-jsx";
import ChatItemDetails from "../../../components/chatItemDetails";
import JsonTree from "../../../components/JsonTree";
import { FiInfo } from "react-icons/fi";

export const commonIconImageStyle = mergeStyles({
  height: 40,
  width: 40,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  marginRight: 10,
});

export interface ConfigDetailsData {
  id: number;
  promptbox: string;
  sql_promptbox: string;
  specific_definitions: string;
  general_definitions: string;
  other_definitions: string;
  nr_defs_used: number;
  config_name: string;
  created_at: string;
  db_schema: string;
  db_tables: string;
  sample_rows_in_table_info: number;
  max_nr_rows_from_sql_query: number;
  nr_samples_used: number;
  db_database: string;
}

// Extended ChatInputData interface for RDM with debug_info
export interface RdmChatInputData extends Omit<ChatInputData, "response"> {
  response: ChatInputData["response"] & {
    debug_info?: {
      method_called: number;
      sql_failed: number;
      sql_passed: number;
      tool_calls_list: string[];
      sql_attempts: unknown[];
      definitions_to_use: string;
      followup_questions: string[];
      autocomplete: unknown[];
      processed_inputs: string;
    };
  };
}

export interface ConfigDetailsStyles {
  container: React.CSSProperties;
  tabBar: React.CSSProperties;
  tabButton: React.CSSProperties;
  tabButtonActive: React.CSSProperties;
  section: React.CSSProperties;
  row: React.CSSProperties;
  label: React.CSSProperties;
  value: React.CSSProperties;
}

interface ConfigDetailsProps {
  configData: ConfigDetailsData;
  styles: ConfigDetailsStyles;
  labels: Partial<Record<keyof ConfigDetailsData, string>>;
  username: string;
  theme: Theme;
  devNumber: number;
  onConfigChange?: (hasUnsavedChanges: boolean) => void;
  onEditedConfigChange?: (editedConfig: ConfigDetailsData) => void;
}

type TabKey = "Agents" | "Definitions" | "Params" | "Chat";

const TABS: TabKey[] = ["Chat", "Agents", "Definitions", "Params"];

export const ConfigDetails: React.FC<ConfigDetailsProps> = ({
  configData,
  styles,
  labels,
  username,
  theme,
  devNumber,
  onConfigChange,
  onEditedConfigChange,
}) => {
  const [activeTab, setActiveTab] = useState<TabKey>("Chat");
  const [chatHistory, setChatHistory] = useState<RdmChatInputData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [ipAddress, setIpAddress] = useState<string>("");
  const [editedConfig, setEditedConfig] =
    useState<ConfigDetailsData>(configData);
  const [editingField, setEditingField] = useState<
    keyof ConfigDetailsData | null
  >(null);
  const [tempValue, setTempValue] = useState<string>("");
  const [showDebugInfo, setShowDebugInfo] = useState<Record<number, boolean>>(
    {}
  );

  // Loading progress state similar to chatPage
  const [loadingProgress, setLoadingProgress] = useState<number>(0);
  const [hasFinished, setHasFinished] = useState<boolean>(false);

  // Refs to track the "real" value and last-change timestamp
  const loadingProgressRef = useRef<number>(0);
  const lastProgressChangeRef = useRef<number>(Date.now());

  const messagesEndRef = useRef<HTMLDivElement>(null);

  const colors = getThemeColors(username, theme);

  useEffect(() => {
    const fetchIpAddress = async () => {
      try {
        const response = await axios.post(`${config.api.baseUrl}/sql/colab`, {
          username,
        });
        if (response.data && response.data.rows.length > 0) {
          setIpAddress(response.data.rows[0].ml_aurora);
        }
      } catch (error) {
        console.error("Error fetching IP address:", error);
      }
    };

    fetchIpAddress();
  }, [username]);

  // Sync editedConfig with configData when it changes
  useEffect(() => {
    setEditedConfig(configData);
  }, [configData]);

  // Check if there are unsaved changes and notify parent
  useEffect(() => {
    const hasChanges =
      JSON.stringify(editedConfig) !== JSON.stringify(configData);
    if (onConfigChange) {
      onConfigChange(hasChanges);
    }
  }, [editedConfig, configData, onConfigChange]);

  // Notify parent of edited config changes
  useEffect(() => {
    if (onEditedConfigChange) {
      onEditedConfigChange(editedConfig);
    }
  }, [editedConfig, onEditedConfigChange]);

  // Auto-scroll to bottom when chat history changes
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollTop = messagesEndRef.current.scrollHeight;
    }
  }, [chatHistory]);

  // Loading progress management
  useEffect(() => {
    let intervalId: NodeJS.Timeout;
    const slowDownpct = 67;

    if (isLoading) {
      // Initialize refs at start of loading
      loadingProgressRef.current = 0;
      lastProgressChangeRef.current = Date.now();
      setLoadingProgress(0);

      intervalId = setInterval(() => {
        const prev = loadingProgressRef.current;
        const now = Date.now();
        let next = prev;

        if (prev < slowDownpct) {
          // Much slower asymptotic approach - take only 2% of remaining distance every 200ms
          next = prev + (slowDownpct - prev) * 0.02;
          next = parseFloat(next.toFixed(2));

          // Update progress and timestamp
          loadingProgressRef.current = next;
          lastProgressChangeRef.current = now;
          setLoadingProgress(next);
        } else {
          // If we've reached or exceeded slowDownpct and been stuck for 2+ seconds, bump by 1%
          if (now - lastProgressChangeRef.current >= 2000) {
            next = Math.min(prev + 1, 95); // Cap at 95% to leave room for the final jump to 100%
            loadingProgressRef.current = next;
            lastProgressChangeRef.current = now;
            setLoadingProgress(next);
          }
        }
      }, 200); // Slower interval: every 200ms instead of 100ms
    }

    return () => {
      clearInterval(intervalId);
    };
  }, [isLoading]);

  const extractHistory = (history: RdmChatInputData[]) => {
    if (history.length === 0) return "";
    return history[history.length - 1].response.history;
  };

  const extractAgentMessages = (history: RdmChatInputData[]) => {
    if (history.length === 0) return "";
    return history[history.length - 1].agent_messages;
  };

  const handleMessageSubmit = async (message: string) => {
    // START LOADING
    setHasFinished(false);
    setIsLoading(true);
    setLoadingProgress(0);
    loadingProgressRef.current = 0;
    lastProgressChangeRef.current = Date.now();

    const definitions_obj = {
      promptbox: editedConfig.promptbox,
      sql_promptbox: editedConfig.sql_promptbox,
      other_definitions: editedConfig.other_definitions,
      specific_definitions: editedConfig.specific_definitions,
      general_definitions: editedConfig.general_definitions,
      nr_defs_used: editedConfig.nr_defs_used,
      db_schema: editedConfig.db_schema,
      db_tables: editedConfig.db_tables,
      sample_rows_in_table_info: editedConfig.sample_rows_in_table_info,
      max_nr_rows_from_sql_query: editedConfig.max_nr_rows_from_sql_query,
      nr_samples_used: editedConfig.nr_samples_used,
      db_database: editedConfig.db_database,
    };

    try {
      const axiosResponse = await axios.post(
        `${config.api.baseUrl}/chat/rdm_passed_in`,
        {
          message,
          username,
          definitions_obj,
          gradio_url: `${ipAddress}/all_passed_in_question`,
          history: extractHistory(chatHistory),
          agent_messages: extractAgentMessages(chatHistory),
        }
      );

      const {
        answer,
        sql,
        history,
        definitions,
        question_id,
        agent_messages,
        debug_info,
      } = axiosResponse.data;

      const savedChat = {
        answer,
        sql,
        history,
        sql_id: 0,
        definitions,
        ...(debug_info && { debug_info }),
      };

      setChatHistory((prevHistory) => [
        ...prevHistory,
        {
          message,
          question_id,
          response: savedChat,
          feedback: {
            display_name: "",
            notes: "",
            thumbs: null,
            feedback: "",
          },
          agent_messages,
        },
      ]);
    } catch (error) {
      let errorMessage = "An error occurred. Please try again.";
      if (axios.isAxiosError(error) && error.response) {
        const data = error.response.data as ErrorResponseData;
        if (data && data.error) {
          errorMessage = data.error;
          if (data.details) {
            errorMessage += ` -- ${data.details}`;
          }
        } else if (error.response.statusText) {
          errorMessage = error.response.statusText;
        }
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      const errorResponse = {
        answer: errorMessage,
        sql: {
          possible_to_answer: false,
          info: "",
        },
        history: "",
        sql_id: 0,
        definitions: [],
      };

      setChatHistory((prevHistory) => [
        ...prevHistory,
        {
          message,
          response: errorResponse,
          feedback: {
            display_name: "",
            notes: "",
            thumbs: null,
            feedback: "",
          },
        },
      ]);
    } finally {
      // END LOADING → snap to 100%, then hide after 300 ms
      setHasFinished(true);
      setTimeout(() => {
        setIsLoading(false);
        setLoadingProgress(0);
        loadingProgressRef.current = 0;
        setHasFinished(false);
      }, 300);
    }
  };

  // Define which fields are editable (definitions_obj fields)
  const editableFields: Set<keyof ConfigDetailsData> = new Set<
    keyof ConfigDetailsData
  >([
    "promptbox",
    "sql_promptbox",
    "specific_definitions",
    "general_definitions",
    "other_definitions",
    "config_name",
    "nr_defs_used",
    "db_schema",
    "db_tables",
    "sample_rows_in_table_info",
    "max_nr_rows_from_sql_query",
    "nr_samples_used",
    "db_database",
  ]);

  const handleStartEdit = (field: keyof ConfigDetailsData) => {
    if (!editableFields.has(field)) return;
    setEditingField(field);
    setTempValue(String(editedConfig[field]));
  };

  const handleSaveEdit = () => {
    if (!editingField) return;

    let newValue: string | number = tempValue;

    // Convert to number for numeric fields
    if (typeof editedConfig[editingField] === "number") {
      const numValue = Number(tempValue);
      if (!isNaN(numValue)) {
        newValue = numValue;
      } else {
        // Invalid number, cancel edit
        handleCancelEdit();
        return;
      }
    }

    setEditedConfig((prev) => ({
      ...prev,
      [editingField]: newValue,
    }));

    setEditingField(null);
    setTempValue("");
  };

  const handleCancelEdit = () => {
    setEditingField(null);
    setTempValue("");
  };

  const renderRow = (
    labelText: string,
    valueText: string | number,
    field?: keyof ConfigDetailsData
  ) => {
    const displayText =
      typeof valueText === "string"
        ? valueText.replace(/\n/g, "\n")
        : String(valueText);

    const isEditable = field && editableFields.has(field);
    const isCurrentlyEditing = editingField === field;

    if (isCurrentlyEditing) {
      return (
        <div style={styles.row}>
          <div style={styles.label}>{labelText}</div>
          <div
            style={{
              ...styles.value,
              display: "flex",
              flexDirection: "column",
              gap: "8px",
            }}
          >
            <textarea
              value={tempValue}
              onChange={(e) => setTempValue(e.target.value)}
              style={{
                width: "100%",
                minHeight:
                  typeof valueText === "string" && valueText.includes("\n")
                    ? "100px"
                    : "60px",
                padding: "8px",
                border: `1px solid ${colors.border}`,
                borderRadius: "4px",
                backgroundColor: colors.background,
                color: colors.text,
                fontFamily: "inherit",
                fontSize: "inherit",
                resize: "vertical",
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && e.ctrlKey) {
                  handleSaveEdit();
                } else if (e.key === "Escape") {
                  handleCancelEdit();
                }
              }}
              autoFocus
            />
            <div style={{ display: "flex", gap: "8px" }}>
              <button
                onClick={handleSaveEdit}
                style={{
                  padding: "4px 12px",
                  backgroundColor: colors.optionsBg,
                  color: colors.text,
                  border: `1px solid ${colors.border}`,
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "12px",
                }}
              >
                Save (Ctrl+Enter)
              </button>
              <button
                onClick={handleCancelEdit}
                style={{
                  padding: "4px 12px",
                  backgroundColor: "transparent",
                  color: colors.text,
                  border: `1px solid ${colors.border}`,
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "12px",
                }}
              >
                Cancel (Esc)
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div style={styles.row}>
        <div style={styles.label}>{labelText}</div>
        <div
          style={{
            ...styles.value,
            whiteSpace: "pre-wrap",
            cursor: isEditable ? "pointer" : "default",
            padding: isEditable ? "4px" : "0",
            borderRadius: isEditable ? "4px" : "0",
            border: isEditable ? `1px solid transparent` : "none",
          }}
          onDoubleClick={() => isEditable && handleStartEdit(field)}
          onMouseEnter={(e) => {
            if (isEditable) {
              e.currentTarget.style.backgroundColor = colors.convoStarterHover;
              e.currentTarget.style.borderColor = colors.border;
            }
          }}
          onMouseLeave={(e) => {
            if (isEditable) {
              e.currentTarget.style.backgroundColor = "transparent";
              e.currentTarget.style.borderColor = "transparent";
            }
          }}
          title={isEditable ? "Double-click to edit" : ""}
        >
          {displayText}
        </div>
      </div>
    );
  };

  const renderIfPresent = <K extends keyof ConfigDetailsData>(key: K) => {
    const label = labels[key];
    if (!label) return null;
    return renderRow(label, editedConfig[key], key);
  };

  const agentsContent = (
    <div style={styles.section}>
      {renderIfPresent("promptbox")}
      {renderIfPresent("sql_promptbox")}
    </div>
  );

  const definitionsContent = (
    <div style={styles.section}>
      {renderIfPresent("other_definitions")}
      {renderIfPresent("specific_definitions")}
      {renderIfPresent("general_definitions")}
    </div>
  );

  const paramsContent = (
    <div style={styles.section}>
      {renderIfPresent("config_name")}
      {renderIfPresent("created_at")}
      {renderIfPresent("id")}
      {renderIfPresent("nr_samples_used")}
      {renderIfPresent("db_database")}
      {renderIfPresent("db_schema")}
      {renderIfPresent("db_tables")}
      {renderIfPresent("nr_defs_used")}
      {renderIfPresent("sample_rows_in_table_info")}
      {renderIfPresent("max_nr_rows_from_sql_query")}
    </div>
  );

  const chatContent = (
    <div style={styles.section}>
      <div
        ref={messagesEndRef}
        style={{
          height: "400px",
          overflowY: "auto",
          marginBottom: "10px",
          paddingBottom: "10px",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          scrollBehavior: "smooth",
          minWidth: 0,
        }}
      >
        {chatHistory.map((chatItem, index) => (
          <div
            key={index}
            style={{ display: "flex", flexDirection: "column", gap: "5px" }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                paddingTop: "5px",
                paddingBottom: "5px",
                border: `1px solid ${colors.border}`,
                gap: "10px",
                borderRadius: "20px",
                backgroundColor: colors.optionsBg,
                padding: "5px 15px",
                width: "fit-content",
                justifyContent: "flex-end",
                alignSelf: "flex-end",
              }}
            >
              {chatItem.message}
            </div>
            {chatItem.response && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  paddingTop: "5px",
                  paddingBottom: "5px",
                  gap: "10px",
                }}
              >
                <div className={commonIconImageStyle}>
                  <img
                    src={getLogoImages(username, theme).collapsed}
                    alt="Logo"
                    style={{ height: "100%", width: "auto" }}
                  />
                </div>
                <div>
                  <Markdown>{chatItem.response.answer}</Markdown>
                </div>
              </div>
            )}
            {chatItem.response && (
              <ChatItemDetails
                username={username}
                chatId={editedConfig.id.toString()}
                theme={theme}
                chatItem={chatItem}
                noFeedback={true}
              />
            )}
            {devNumber > 0 &&
              devNumber < 99 &&
              chatItem.response?.debug_info && (
                <div style={{ marginTop: "8px" }}>
                  <button
                    onClick={() =>
                      setShowDebugInfo((prev) => ({
                        ...prev,
                        [index]: !prev[index],
                      }))
                    }
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      padding: "6px 12px",
                      backgroundColor: colors.optionsBg,
                      border: `1px solid ${colors.border}`,
                      borderRadius: "6px",
                      color: colors.text,
                      cursor: "pointer",
                      fontSize: "12px",
                      fontFamily: "inherit",
                    }}
                  >
                    <FiInfo size={14} />
                    Debug Info
                    <span style={{ marginLeft: "4px" }}>
                      {showDebugInfo[index] ? "▼" : "▶"}
                    </span>
                  </button>
                  {showDebugInfo[index] && (
                    <JsonTree
                      data={chatItem.response.debug_info}
                      theme={theme}
                      username={username}
                    />
                  )}
                </div>
              )}
          </div>
        ))}
      </div>
      <ChatInput
        theme={theme}
        onMessageSubmit={handleMessageSubmit}
        isDisabled={isLoading}
        placeholderText="Ask a question about this configuration..."
        username={username}
      />

      {/* Loading progress bar */}
      <div
        style={{
          textAlign: "center",
          marginTop: "10px",
          height: "25px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {isLoading ? (
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              color: colors.text,
              gap: "10px",
            }}
          >
            {/* Show percentage number */}
            <div
              style={{
                fontSize: "14px",
                fontWeight: "500",
                minWidth: "35px",
              }}
            >
              {hasFinished
                ? // When finished, show 100% briefly
                  "100%"
                : `${Math.round(loadingProgress)}%`}
            </div>

            {/* Bar track */}
            <div
              style={{
                width: "100px",
                height: "4px",
                backgroundColor: "rgba(59, 145, 97, 0.3)",
                borderRadius: "2px",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  width: `${hasFinished ? 100 : Math.max(loadingProgress, 0)}%`,
                  height: "100%",
                  backgroundColor: colors.text,
                  transition: hasFinished
                    ? "width 0.2s ease-in"
                    : "width 0.1s ease-out",
                }}
              />
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );

  const contentByTab: Record<TabKey, React.ReactNode> = {
    Chat: chatContent,
    Agents: agentsContent,
    Definitions: definitionsContent,
    Params: paramsContent,
  };

  return (
    <div style={styles.container}>
      <div style={styles.tabBar}>
        {TABS.map((tab) => (
          <button
            key={tab}
            style={
              activeTab === tab ? styles.tabButtonActive : styles.tabButton
            }
            onClick={() => setActiveTab(tab)}
          >
            {tab}
          </button>
        ))}
      </div>
      {contentByTab[activeTab]}
    </div>
  );
};

export default ConfigDetails;
